/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose, Query } from 'react-apollo'
import gql from 'graphql-tag'
import { withRouter } from 'react-router-dom'

import {
  Spinner,
  Button,
  IdentityContactDetails,
  IdentityBankAccounts,
  IdentityFiles,
  EmployerContacts,
  EmployerDivisions,
  Heading,
  SvgIcon,
  MetricComparisonTable,
  EmployerSolutionProducts,
  EmployerNotes,
  EmployerKeyStatistics,
  EmployerEmployees,
  Account,
  IdentityWhatsAppSession,
  PayrollCycleAccessRights
} from '../'

import {
  Requests,
  Case,
  CaseList,
  PayrollRunList,
  Communications,
  Accounts,
  Request,
  WhatsAppMessage
} from '../../containers'

import {
  decodeBase64,
  upperCaseFirstCharacter
} from '../../utils'

import {
  METRIC_EMPLOYER_DAILY_SUMMARY
} from '../../constants'

const setStateDefault = (activeTabIndex = 0) => ({
  casesOrderBy: '-created_at',
  payrollRunsOrderBy: '-paid_on',
  loanApplicationOrderBy: '-created_at',
  showCreateCaseModal: false,
  showKeyStats: false
})

class EmployerIdentityDetail extends React.PureComponent {
  static propTypes = {
    identityId: PropTypes.string.isRequired,
    detail: PropTypes.string.isRequired,
    objectId: PropTypes.string
  }

  state = setStateDefault()

  renderDetail = (detail, objectId) => {
    let identityId = this.props.identityId
    let currentPath = this.props.location.pathname

    const identity = this.props.data.identity

    // three metric related constants follow
    const metricObjectId = null  // for employer metrics, metric query uses identity id instead of the related object id
                                // see fetchMetricsAcrossDateRange (src/components/metric-comparison-table/index.js) & ObjectMetric component's query setup
    const metricIdentityId = this.props.data.identity.id

    const metricSourceId = METRIC_EMPLOYER_DAILY_SUMMARY

    switch(detail) {
      case 'summary':
        return (
          <div className='slds-m-top_x-large slds-m-left_x-large slds-m-right_x-large'>
            <EmployerNotes
              employerId={identity.employer.id}
              className='slds-m-bottom_x-large'
            />
            {this.state.showKeyStats
              ? <EmployerKeyStatistics employerId={identity.employer.id} />
              : <Button onClick={() => this.setState({ showKeyStats: true })}>
                  Show key statistics
                </Button>
            }
          </div>
        )

      case 'contact':
        return (
          <div className='slds-size_3-of-4 slds-col-rule_right' style={{ paddingTop: '2%', margin: 'auto' }}>
            <IdentityContactDetails identityId={identityId} />
          </div>
        )

      case 'banking':
        return (
          <div className='slds-size_3-of-4 slds-col-rule_right' style={{ paddingTop: '2%', margin: 'auto' }}>
            <IdentityBankAccounts identityId={identityId} />
          </div>
        )

      case 'requests':
        if (objectId) {
          return <Request id={objectId} />
        } else {
          return (
            <Requests
              title={null}
              identityId={identityId}
              statusFilterInSidebar={true}
              showRequestStatusColumn={true}
            />
          )
        }

      case 'cases':
        if (objectId) {
          return <Case id={objectId} className='slds-border_top' />
        } else {
          return (
            <CaseList
              affectsId={identityId}
              urlPath={currentPath}
              statusFilterInSidebar={true}
              recordsPerPage={10}
              title={null}
              showStatusColumn={true}
              autoAssignToCaseHandler={false}
            />
          )
        }

      case 'documents':
        return <IdentityFiles identity={identityId} />

      // comms
      case 'messages':
      case 'emails':
      case 'whatsapp':
        return (
          <Query
            query={
              gql`
                query($uuid: UUID!, $addressType: String!) {
                  addresses(identity_Id: $uuid, addressType: $addressType) {
                    edges {
                      node {
                        id
                        addressType
                      }
                    }
                  }
                }
              `
            }
            variables={{
              uuid: decodeBase64(identityId),
              addressType: detail === 'emails'
                ? 'email'
                : 'msisdn'
            }}>
            {({ loading, error, data }) => {
              if (loading || error) {
                return null
              }

              let addresses = data.addresses.edges
              if (addresses.length > 0) {
                return (
                  <div className='u-bg-color-white'>
                    {detail === 'whatsapp' &&
                      <IdentityWhatsAppSession
                        identityId={identityId}
                        title='Session info'
                        className='slds-p-left_small slds-p-top_small'
                      />
                    }
                    {objectId
                      ? <WhatsAppMessage messageId={objectId} />
                      : <Communications
                          identityId={identityId}
                          communicationType={detail === 'whatsapp'
                            ? 'WhatsApp'
                            : upperCaseFirstCharacter(detail)  // Messages / Emails
                          }
                          urlPath={currentPath}
                          title={null}
                          noStatusFilter={true}
                          key={detail}
                        />
                      }
                  </div>
                )
              } else {
                return (
                  <div className='slds-m-top_large slds-m-left_large'>
                    <SvgIcon
                      variant='utility'
                      symbol='info'
                      colour='light'
                      size='small'
                      className='slds-m-right_medium'
                    />
                    {`Identity has no ${detail === 'messages' ? 'cellphone' : 'email'} address.`}
                  </div>
                )
              }
            }}
          </Query>
        )

      case 'accounts':
        if (objectId) {
          return <Account id={objectId} />
        } else {
          return (
            <Accounts
              identityId={identityId}
              title={null}
              urlPath={currentPath}
            />
          )
        }


      case 'contacts':
        return (
          <EmployerContacts
            employerId={decodeBase64(this.props.data.identity.employer.id)}
            employer={this.props.data.identity.employer.id}
            className='slds-m-around_x-small'
          />
        )

      case 'divisions':
        return <EmployerDivisions employer={this.props.data.identity.employer.id} />

      case 'employees':
        return <EmployerEmployees employerId={this.props.data.identity.employer.id} />

      case 'payrollcycles':
        if (objectId) {
          return (
            <div className='slds-p-top_medium u-bg-color-white'>
              <PayrollCycleAccessRights
                payrollCycleId={objectId}
                className='slds-m-bottom_medium'
              />
              <PayrollRunList
                title={
                  <div className='slds-grid'>
                    <SvgIcon
                      variant='utility'
                      symbol='moneybag'
                      size='small'
                      colour='light'
                      className='slds-m-left-small slds-m-right_small'
                    />
                    <Heading marginBottom={0}>
                      Payroll runs
                    </Heading>
                  </div>
                }
                employerId={this.props.data.identity.employer.id}
                payrollCycleId={objectId}
                statusFilterInSidebar={true}
                initialStatusFilterValue='all'
                recordsPerPage={10}
              />
            </div>
          )
        } else {
          return null
        }

      // insights sub-tabs: latest, comparison
      case 'latest':
        return (
          <div className='slds-p-left_medium slds-p-top_large'>
            <SvgIcon
              variant='utility'
              symbol='info'
              size='small'
              colour='light'
              className='slds-m-right_x-small'
            />
            {/* this.message will become redundant when we can show latest key indicators */}
            Coming soon... For now, use comparison tab to view and compare metrics.
          </div>
        )

      case 'comparison':
        const renderTemplate = {
          "Payroll": {
            "Frequency": {
              "null": 'NumberField',
              "Every week": 'NumberField',
              "Once a month": 'NumberField',
              "Every second week": 'NumberField'
            }
          },
          "Solution": {
            "Package": {
              "null": 'NumberField',
              "basic": 'NumberField',
              "premium": 'NumberField',
              "standard": 'NumberField'
            }
          },
          "EarnBacks": {
            "null": 'NumberField',
            "Active": 'NumberField',
            "Inactive": 'NumberField',
            "Ineligible": 'NumberField',
            "Outstanding": 'NumberField'
          },
          "Employment": {
            "Status": {
              "Active": 'NumberField',
              "Pending": 'NumberField',
              "Suspended": 'NumberField',
              "Non-active": 'NumberField',
              "Terminated - Quit": 'NumberField',
              "Terminated - Fired": 'NumberField',
              "Terminated - Other": 'NumberField',
              "Terminated - Retired": 'NumberField',
              "Terminated - Deceased": 'NumberField',
              "Terminated - Absconded": 'NumberField',
              "Terminated - Seasonal Retrenched": 'NumberField',
              "Terminated - Permanent Retrenched": 'NumberField'
            }
          },
          "Membership": {
            "Member": "NumberField",
            "Non-Member": "NumberField"
          },
          "Health Level": {
            "0": "NumberField",
            "1": "NumberField",
            "2": "NumberField",
            "4": "NumberField"
          }
        }

        return (
          <MetricComparisonTable
            objectId={metricObjectId}
            identityId={metricIdentityId}
            metricSourceId={metricSourceId}
            className='slds-m-around_medium'
            renderTemplate={renderTemplate}
          />
        )

      case 'solution':
        return (
          <EmployerSolutionProducts identityId={identityId} className='slds-p-around_medium' />
        )

      default:
        return null
    }
  }

  render() {
    if (this.props.data.loading) {
      return <Spinner className='slds-is-relative' />
    }

    if (this.props.data.error) {
      return <h1>Oh dear! We have an issue</h1>
    }

    return this.renderDetail(this.props.detail, this.props.objectId)
  }
}

const getIdentityDetails = gql`
  query($id: ID!) {
    identity(id: $id) {
      id
      fullName
      identityValue
      membershipStatus
      client {
        id
        status
        division {
          id
          employer {
            id
            friendlyName
          }
        }
        payrollcycle {
          id
          cycle
        }
      }
      contacts {
        edges {
          node {
            id
          }
        }
      }
      employer {
        id
        friendlyName
        employergroup {
          id
          friendlyName
        }
      }
    }
  }
`

const withData =
  graphql(getIdentityDetails, {
    options: ({ identityId }) => ({
      variables: {
        id: identityId
      }
    })
  })

export default compose(
  withData,
  withRouter
)(EmployerIdentityDetail)



// WEBPACK FOOTER //
// ./src/components/employer-identity-detail/index.js